import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Stethoscope,
  Building2,
  Users,
  Calendar,
  TrendingUp,
  LogOut,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const planColors = {
  trial: "bg-slate-100 text-slate-700 border-slate-200",
  basic: "bg-blue-50 text-blue-700 border-blue-200",
  pro: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  platform: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c, u, t] = await Promise.all([
        api.get("/admin/summary"),
        api.get("/admin/clinics", { params: { limit: 200 } }),
        api.get("/admin/users", { params: { limit: 200 } }),
        api.get("/admin/transactions", { params: { limit: 200 } }),
      ]);
      setSummary(s.data);
      setClinics(c.data.items || c.data);
      setUsers(u.data.items || u.data);
      setTransactions(t.data.items || t.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const updatePlan = async (clinic_id, plan) => {
    try {
      await api.put(`/admin/clinics/${clinic_id}/plan?plan=${plan}`);
      toast.success(`Updated to ${plan}`);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  if (loading || !summary) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
                HeDesk Admin
              </div>
              <div className="text-xs text-slate-500">Platform control panel</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:inline">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Platform overview
          </h1>
          <p className="text-slate-600 mt-1">All clinics, users, and revenue in one place.</p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-slate-200" data-testid="admin-stat-clinics">
            <CardContent className="p-6">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-4"><Building2 className="w-5 h-5 text-blue-600" /></div>
              <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{summary.clinics_count}</div>
              <div className="text-sm text-slate-500 mt-1">Total clinics</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="admin-stat-users">
            <CardContent className="p-6">
              <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center mb-4"><Users className="w-5 h-5 text-sky-600" /></div>
              <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{summary.users_count}</div>
              <div className="text-sm text-slate-500 mt-1">Total users</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="admin-stat-appointments">
            <CardContent className="p-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center mb-4"><Calendar className="w-5 h-5 text-emerald-600" /></div>
              <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{summary.appointments_count}</div>
              <div className="text-sm text-slate-500 mt-1">Appointments</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="admin-stat-revenue">
            <CardContent className="p-6">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center mb-4"><TrendingUp className="w-5 h-5 text-amber-600" /></div>
              <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{formatINR(summary.total_revenue)}</div>
              <div className="text-sm text-slate-500 mt-1">Subscription revenue</div>
            </CardContent>
          </Card>
        </div>

        {/* By plan */}
        <Card className="border-slate-200 mb-8">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Plan distribution</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Object.entries(summary.by_plan || {}).map(([plan, count]) => (
                <div key={plan} className="px-4 py-3 rounded-xl bg-slate-50 border border-slate-200">
                  <div className="text-xs uppercase text-slate-500 font-semibold tracking-wider">{plan}</div>
                  <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>{count}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="clinics">
          <TabsList className="mb-4">
            <TabsTrigger value="clinics" data-testid="tab-clinics">Clinics</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          </TabsList>

          <TabsContent value="clinics">
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Clinic</TableHead>
                      <TableHead>Users</TableHead>
                      <TableHead>Patients</TableHead>
                      <TableHead>Appts</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clinics.map((c) => (
                      <TableRow key={c.clinic_id} data-testid={`clinic-row-${c.clinic_id}`}>
                        <TableCell>
                          <div className="font-semibold text-slate-900">{c.name}</div>
                          <div className="text-xs text-slate-500 font-mono">{c.clinic_id}</div>
                        </TableCell>
                        <TableCell>{c.user_count}</TableCell>
                        <TableCell>{c.patient_count}</TableCell>
                        <TableCell>{c.appointment_count}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={planColors[c.plan] || planColors.trial}>
                            {c.plan}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {new Date(c.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                        </TableCell>
                        <TableCell className="text-right">
                          <Select value={c.plan} onValueChange={(v) => updatePlan(c.clinic_id, v)}>
                            <SelectTrigger className="w-32 h-8 text-xs ml-auto" data-testid={`plan-select-${c.clinic_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="trial">Trial</SelectItem>
                              <SelectItem value="basic">Basic</SelectItem>
                              <SelectItem value="pro">Pro</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Clinic</TableHead>
                      <TableHead>Provider</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{u.role?.replace("_", " ")}</Badge>
                        </TableCell>
                        <TableCell className="text-xs font-mono text-slate-500">{u.clinic_id}</TableCell>
                        <TableCell><span className="text-xs capitalize">{u.provider}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transactions">
            <Card className="border-slate-200">
              <CardContent className="p-0">
                {transactions.length === 0 ? (
                  <div className="p-12 text-center text-slate-500 text-sm">No transactions yet.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Session</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((t) => (
                        <TableRow key={t.session_id} data-testid={`txn-row-${t.session_id}`}>
                          <TableCell className="text-xs font-mono text-slate-500">{t.session_id?.slice(-12)}</TableCell>
                          <TableCell className="text-sm">{t.user_email}</TableCell>
                          <TableCell><Badge variant="outline" className="capitalize">{t.plan_id}</Badge></TableCell>
                          <TableCell className="text-right font-semibold">{formatINR(t.amount)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={t.payment_status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
                              {t.payment_status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">
                            {new Date(t.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
